import { useEffect, useRef } from "react";

const EMOJI_GROUPS = [
  {
    label: "Faces",
    emojis: ["😀","😂","🥲","😊","😍","🥰","😎","🤔","😢","😭","😤","😡","🥺","😬","🫠","🤗","😴","🤩","🥳","😏"],
  },
  {
    label: "Gestures & Hearts",
    emojis: ["👍","👎","👌","🤌","🙏","🤝","👋","✌️","🤞","💪","👏","🫶","❤️","🧡","💛","💚","💙","💜","🖤","🤍"],
  },
  {
    label: "Celebration & Nature",
    emojis: ["🎉","🎊","🔥","✨","💫","⭐","🌟","💯","🏆","🥇","🌸","🌺","🌻","💐","🌈","❄️","🌙","☀️","🌊","🍀"],
  },
  {
    label: "Food & Things",
    emojis: ["😸","🦋","🐾","🎨","💉","🩹","🍕","🍔","🍣","🥂","🍷","☕","🧋","🎵","🎶","📷","📱","💻","💰","💸"],
  },
  {
    label: "Symbols",
    emojis: ["✅","❌","❓","❗","💬","📍","📌","🔒","🔑","✏️","📝","💡","🔔","🚨","📞","📧","🗓️","⚡","🔄","👀"],
  },
];

export function insertEmojiAtCursor(
  emoji: string,
  text: string,
  setText: (s: string) => void,
  ref: React.RefObject<HTMLTextAreaElement | null>,
) {
  const ta = ref.current;
  if (!ta) {
    setText(text + emoji);
    return;
  }
  const start = ta.selectionStart ?? text.length;
  const end = ta.selectionEnd ?? text.length;
  const newText = text.slice(0, start) + emoji + text.slice(end);
  setText(newText);
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(start + emoji.length, start + emoji.length);
  });
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  align?: "left" | "right";
}

export function EmojiPicker({ onSelect, onClose, align = "left" }: EmojiPickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute bottom-full mb-2 z-50 rounded-2xl shadow-2xl overflow-hidden"
      style={{
        [align === "right" ? "right" : "left"]: 0,
        width: 280,
        background: "#18181b",
        border: "1px solid #3f3f46",
      }}
      data-testid="emoji-picker"
    >
      <div className="overflow-y-auto" style={{ maxHeight: 220 }}>
        {EMOJI_GROUPS.map((group) => (
          <div key={group.label} className="px-2 pt-2">
            <p className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wide px-1 mb-1">
              {group.label}
            </p>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-0.5 pb-1">
              {group.emojis.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => { onSelect(emoji); onClose(); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-lg hover:bg-zinc-700 transition-colors"
                  data-touch-exempt="dense-grid"
                  data-testid={`emoji-btn-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
